<template>
  <page>
    <lazy-div>
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon v-if="newMode">mdi-plus</v-icon>
          <v-icon v-else-if="reviewMode">mdi-pencil</v-icon>
          <v-icon v-else-if="queryMode">mdi-magnify</v-icon>
          <v-toolbar-title v-if="newMode">Create New Campaign</v-toolbar-title>
          <v-toolbar-title v-else-if="reviewMode">Edit Campaign</v-toolbar-title>
          <v-toolbar-title v-else-if="queryMode">Query Campaign</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container fluid grid-list-lg>
            <v-layout row wrap>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ labels.campaign }}</v-subheader>
                <div class="queryValue">{{ form.campaign }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.campaign"
                  :label="labels.campaign"
                  :error-messages="errors.collect('campaign')"
                  data-vv-name="campaign"
                  :data-vv-as="labels.campaign"
                  v-validate="'required|max:12'"
                ></v-text-field>
              </v-flex>

              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.description }}</v-subheader>
                <div class="queryValue">{{ form.description }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.description"
                  :label="labels.description"
                  :error-messages="errors.collect('description')"
                  data-vv-name="description"
                  :data-vv-as="labels.description"
                  v-validate="'required|max:128'"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.effDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(cloneQueryEffDate) }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <cal-picker
                  :label="labels.effDate"
                  v-model="form.effDate"
                  :error-messages="errors.collect('effDate')"
                  data-vv-name="effDate"
                  :data-vv-as="labels.effDate"
                  v-validate="effDateValidation"
                ></cal-picker>
              </v-flex>

              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.expDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(cloneQueryExpDate) }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <cal-picker
                  :label="labels.expDate"
                  v-model="form.expDate"
                  :error-messages="errors.collect('expDate')"
                  data-vv-name="expDate"
                  :data-vv-as="labels.expDate"
                  v-validate="expDateValidation"
                ></cal-picker>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-checkbox readonly v-model="form.campCommDisc" :label="labels.campCommDisc"></v-checkbox>
              </v-flex>
              <v-flex xs6 v-else>
                <v-checkbox
                  v-model="form.campCommDisc"
                  :label="labels.campCommDisc"
                  :error-messages="errors.collect('campCommDisc')"
                  data-vv-name="campCommDisc"
                  :data-vv-as="labels.campCommDisc"
                  v-validate="'required'"
                ></v-checkbox>
              </v-flex>

              <v-flex xs6 v-if="queryMode">
                <v-checkbox readonly v-model="form.allIntermediary" :label="labels.allIntermediary"></v-checkbox>
              </v-flex>
              <v-flex xs6 v-else>
                <v-checkbox
                  v-model="form.allIntermediary"
                  :label="labels.allIntermediary"
                  :error-messages="errors.collect('allIntermediary')"
                  data-vv-name="allIntermediary"
                  :data-vv-as="labels.allIntermediary"
                  v-validate="'required'"
                ></v-checkbox>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-checkbox readonly v-model="form.enabled" :label="labels.enabled"></v-checkbox>
              </v-flex>
              <v-flex xs6 v-else>
                <v-checkbox
                  v-model="form.enabled"
                  :label="labels.enabled"
                  :error-messages="errors.collect('enabled')"
                  data-vv-name="enabled"
                  :data-vv-as="labels.enabled"
                  v-validate="'required'"
                ></v-checkbox>
              </v-flex>
            </v-layout>
          </v-container>
          <v-container fluid grid-list-lg>
            <v-layout>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
                <div class="queryValue">{{ createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(createdDate) }}</div>
              </v-flex>
            </v-layout>
            <v-layout v-if="!newMode">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ modifiedBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(modifiedDate) }}</div>
              </v-flex>
            </v-layout>
          </v-container>
          <v-spacer class="mt-3"></v-spacer>
          <v-container fluid grid-list-lg>
            <v-btn class="mr-2" color="error" @click="doCancel" :loading="submitting">
              <span>Cancel</span>
              <v-icon right dark>mdi-cancel</v-icon>
            </v-btn>
            <v-btn :disabled="queryMode" color="primary" dark class="mr-2" @click="doSave" :loading="submitting">
              {{ newMode ? 'Save' : '' }}{{ reviewMode ? 'Update' : '' }}
              <v-icon right>mdi-content-save</v-icon>
            </v-btn>
          </v-container>
        </v-card-text>
      </v-card>
    </lazy-div>
  </page>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { formatDisplayDate, removeTime } from '@/utils/date'
import clone from 'clone'
export default {
  data() {
    return {
      labels: {
        campaign: 'Campaign',
        description: 'Description',
        effDate: 'Effective Date',
        expDate: 'Expiry Date ',
        campCommDisc: 'Campaign Commission/Discount',
        allIntermediary: 'Permit All Intemediary',
        enabled: 'Enabled',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      form: {
        actionKey: 'CAMPAIGN',
        campaign: '',
        description: '',
        effDate: new Date(),
        expDate: new Date(),
        campCommDisc: false,
        allIntermediary: true,
        enabled: false
      },
      cloneQueryEffDate: '',
      cloneQueryExpDate: '',
      newMode: false,
      queryMode: false,
      reviewMode: false,
      submitting: false,
      createdBy: this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName,
      createdDate: new Date(),
      modifiedBy: '',
      modifiedDate: undefined
    }
  },
  methods: {
    formatDisplayDate(xDate) {
      return formatDisplayDate(xDate)
    },
    async doFetch() {
      const apiURI = this.menu.getDetail
      if (apiURI) {
        this.loading = true
        try {
          const result = await this.$http.put(apiURI, this.objParam)
          const { data } = result.data
          if (data) {
            this.form.actionKey = data.campaignDto.actionKey
            this.form.campaign = data.campaignDto.campaign
            this.form.description = data.campaignDto.description
            this.form.effDate = data.campaignDto.effDate
            this.form.expDate = data.campaignDto.expDate
            this.form.campCommDisc = data.campaignDto.campCommDisc
            this.form.allIntermediary = data.campaignDto.allIntermediary
            this.form.enabled = data.campaignDto.enabled
            this.createdBy = data.createdBy
            this.createdDate = data.createdDate
            this.modifiedBy = data.modifiedBy
            this.modifiedDate = data.modifiedDate
          }
          this.cloneQueryEffDate = clone(this.form.effDate)
          this.cloneQueryExpDate = clone(this.form.expDate)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doCancel() {
      this.$router.push({ name: 'campaignList' })
    },
    doSave() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newMode) {
            this.doCreate()
          } else if (this.reviewMode) {
            this.doUpdate()
            // } else {
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doCreate() {
      const apiURI = this.menu.doCreate
      if (apiURI) {
        this.submitting = true
        try {
          this.form.effDate = removeTime(this.form.effDate)
          this.form.expDate = removeTime(this.form.expDate)
          const result = await this.$http.post(apiURI, this.form)
          const { data } = result
          if (data) {
            this.notify(data)
            this.message(data)
            this.fieldMessage(data)
            this.doCancel()
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doUpdate() {
      const apiURI = this.menu.doUpdate
      if (apiURI) {
        this.submitting = true
        try {
          this.form.effDate = removeTime(this.form.effDate)
          this.form.expDate = removeTime(this.form.expDate)
          const result = await this.$http.put(apiURI, this.form)
          const { data } = result
          if (data) {
            this.notify(data)
            this.doCancel()
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response.data
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    }
  },
  computed: {
    menu() {
      return this.$store.state.campaignParam.menu
    },
    modeParam() {
      return this.$route.params.paramMode
    },
    objParam() {
      return this.$route.params.paramObjRes
    },
    effDateValidation() {
      return 'required'
    },
    expDateValidation() {
      if (this.form.expDate !== undefined && this.form.expDate !== '') {
        return 'required|start_enddate:' + this.form.effDate
      } else {
        return 'required'
      }
    }
  },
  created() {
    if (this.modeParam === 'create') {
      this.newMode = true
    } else if (!this.modeParam) {
      this.doCancel()
    } else {
      if (this.modeParam === 'query') {
        this.queryMode = true
      } else if (this.modeParam === 'review') {
        this.reviewMode = true
      }
      this.doFetch()
    }
  },
  mixins: [MessageMixin]
}
</script>
